import {
  memo,
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { Button, Icons, StatusDot } from "@/components/ui";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import type { FrameType } from "@/api/spaces";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { cn } from "@/lib/cn";
import {
  FOCUS_FRAME_DRAG_HEIGHT,
  FOCUS_FRAME_DRAG_OUTSET,
} from "@/lib/frame-window-gestures";
import {
  conversationFrameDisplayTitle,
  isUntitledChatTitle,
} from "@/lib/conversation-display";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { answerInInputBar } from "@/lib/answer-in-input-bar";
import { dismissFrameError } from "@/lib/dismiss-frame-error";
import { dismissFrameFromFocusPresentation } from "@/lib/focus-frame-dismissal";
import { backToWorkspace } from "@/lib/focus/focus-back";
import { closeToCard } from "@/lib/focus/focus-card-return";
import { useFrameWindow } from "@/components/focus/frame-window-context";
import { ConversationArtifactMenu } from "@/components/shapes/conversation-frame/conversation-artifact-menu";
import { conversationFrameTaskId } from "@/components/shapes/conversation-frame/conversation-frame-utils";
import { useNotificationStore } from "@/stores/notification-store";
import { useFrameEditController } from "./frame-edit-controller";
import { hideFrameFromCanvas } from "./use-frame-actions";
import {
  NO_MENU_EXTRAS,
  useFrameChromeConfigValue,
} from "./frame-chrome-config";
import { FrameChromeTools } from "./frame-chrome-tools";
import { frameAttentionText, useFrameAttention } from "./use-frame-attention";
import { useOpenedFrame } from "./use-opened-frame";
import {
  useFrameRenderer,
  type FrameChromeGeometry,
  type FrameItemMenuProps,
} from "./frame-renderer-context";
import type { FrameData } from "@/lib/frame-data";

// Headerless frames with nothing clickable in their top 24px get a taller
// invisible canvas grab strip; every other frame keeps the 10px strip so it
// never covers pinned top controls (note format bar, terminal tools, ...).
const TALL_DRAG_STRIP_FRAME_TYPES: ReadonlySet<FrameType> = new Set([
  "todo",
  "image",
  "connection",
  "connection_prompt",
]);

interface FrameHoverChromeProps {
  frameId: string;
  frame: FrameData;
  frameRootRef: RefObject<HTMLDivElement | null>;
  bottomActive: boolean;
  topActive: boolean;
  onBottomActiveChange: (active: boolean) => void;
  onTopActiveChange: (active: boolean) => void;
  actionFrameId?: string;
  inputContext?: CursorInputContextValue | null;
  onCloseFrame?: () => void;
  onConversationArtifactPresented?: () => void;
  /** A window-bound surface's ⋯ (the docked chat), drawn before ← and ×. */
  renderItemMenu?: (props: FrameItemMenuProps) => ReactNode;
  snoozeBottom?: () => void;
}

interface FrameChromeMeasureProps {
  frameId: string;
  rootRef: RefObject<HTMLDivElement | null>;
  children: (geometry: FrameChromeGeometry) => ReactNode;
}

/** A window-bound surface keeps its chrome inside, at screen scale. */
const DETACHED_CHROME_GEOMETRY: FrameChromeGeometry = {
  inside: true,
  scale: 1,
};

// Only focused and auxiliary chrome reads geometry, so ordinary canvas frames
// never subscribe to the viewport or measure their layout.
function FrameChromeMeasure({
  frameId,
  rootRef,
  children,
}: FrameChromeMeasureProps) {
  return children(useFrameRenderer().useChromeGeometry(frameId, rootRef));
}

export const FrameHoverChrome = memo(function FrameHoverChrome({
  frameId,
  frame,
  frameRootRef,
  topActive,
  onTopActiveChange,
  actionFrameId = frameId,
  onCloseFrame,
  onConversationArtifactPresented,
  renderItemMenu,
}: FrameHoverChromeProps) {
  const t = useT(framesSharedMessages);
  const [artifactOpen, setArtifactOpen] = useState(false);
  const [toolOpen, setToolOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const config = useFrameChromeConfigValue();
  const attention = useFrameAttention(actionFrameId);
  const isConversation = frame.frameType === "conversation";
  const linkedTaskId = isConversation
    ? conversationFrameTaskId(frame)
    : undefined;
  const hoverTitle = isConversation
    ? conversationFrameDisplayTitle(
        isUntitledChatTitle(frame.title) ? t("newChat") : frame.title,
      )
    : frameDisplayTitle(frame);
  const editController = useFrameEditController(actionFrameId);
  const renderer = useFrameRenderer();
  const chrome = renderer.useChromeState(actionFrameId);
  const closeWindow = renderer.closeFrame;
  // Solid windows keep their hover pills off the native glass path.
  const pillGlassForce = renderer.material === "solid" ? "css" : undefined;
  const windowed = closeWindow !== undefined;
  // A frame window's shell drags from the top band of its paper
  // (`frame-window-band.ts`), so its strip stops at the paper's edge.
  const inFrameWindow = useFrameWindow() !== null;
  // A window-bound surface passes its own close handler: it is auxiliary
  // chrome, never focused, and keeps detached geometry.
  const auxiliary = Boolean(onCloseFrame);
  const focused = !auxiliary && chrome.focused;
  const maximized = chrome.maximized;
  const preview = chrome.preview;
  // Opened-window rule 1: `[Answer · type tools · Edit] │ ⋯ · ⛶ · ← · ×`.
  // The frame's own tools lead the pill on opened windows, a read-only
  // preview's too: the opened frame draws none of its own (`useOpenedFrame`).
  const opened = useOpenedFrame();
  const leadTools = (auxiliary || opened) && !linkedTaskId;
  const toolsShown = leadTools && config.tools.length > 0;
  // Answer and Dismiss reach a read-only preview too: they act on the
  // request, not on the frame's content.
  const attentionShown =
    leadTools &&
    (attention?.kind === "question" ||
      (attention?.kind === "error" &&
        !config.error &&
        frame.frameType !== "terminal"));
  const artifactsShown = leadTools && isConversation && !preview;
  const editShown =
    leadTools && (editController.editing || editController.available);
  const anyTool = attentionShown || toolsShown || artifactsShown || editShown;
  // The item's ⋯: the surface's on a focused window, the caller's on a
  // window-bound one (the docked chat).
  const itemMenu = auxiliary
    ? renderItemMenu
    : focused
      ? renderer.renderMenu
      : undefined;
  const closeFrame = useMemo(
    () =>
      onCloseFrame ??
      (closeWindow ? () => closeWindow(actionFrameId) : undefined),
    [actionFrameId, closeWindow, onCloseFrame],
  );
  const shown =
    topActive ||
    (leadTools && (toolOpen || artifactOpen)) ||
    (auxiliary && editController.editing);
  const tone =
    attention?.kind === "question"
      ? "question"
      : config.error || attention?.kind === "error"
        ? "error"
        : null;
  const stateText =
    attention?.kind === "question"
      ? frameAttentionText(attention)
      : config.error || frameAttentionText(attention);
  const tooltip = [hoverTitle, stateText, config.titleHint]
    .filter(Boolean)
    .join("\n");
  const handleBack = useCallback(
    () =>
      closeToCard(() =>
        backToWorkspace(actionFrameId, {
          frame,
          returnInFocus: !windowed && focused,
          beforeExit: closeFrame,
        }),
      ),
    [actionFrameId, closeFrame, focused, frame, windowed],
  );
  const handleClose = useCallback(() => {
    if (closeFrame) closeFrame();
    else
      closeToCard(() => {
        if (!dismissFrameFromFocusPresentation(actionFrameId))
          hideFrameFromCanvas(actionFrameId);
      });
  }, [actionFrameId, closeFrame]);
  const handleAttention = async () => {
    setPending(true);
    try {
      if (attention?.kind === "question")
        await answerInInputBar(attention.awaitingInputId);
      else await dismissFrameError(actionFrameId);
    } catch (error) {
      useNotificationStore.getState().addSystemToast({
        id: `frame-attention-${actionFrameId}`,
        title: t("updateFailed"),
        description: error instanceof Error ? error.message : t("tryAgain"),
        level: "error",
      });
    } finally {
      setPending(false);
    }
  };
  const actionButton = (
    id: string,
    label: string,
    Icon: typeof Icons.X,
    onClick: () => void,
    pressed?: boolean,
  ) => (
    <Button
      type="button"
      variant="ghost"
      size="iconSm"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      data-testid={`frame-chrome-${id}`}
    >
      <Icon className="h-3.5 w-3.5" />
    </Button>
  );
  // Let border resize controls win where the outward drag target overlaps them.
  const dragStrip = (scale: number) => (
    <div
      className={cn(
        "absolute cursor-grab active:cursor-grabbing",
        focused && !maximized ? "z-[65]" : "z-[75]",
        focused ? "inset-x-0" : "inset-x-3 top-0",
        !focused &&
          (TALL_DRAG_STRIP_FRAME_TYPES.has(frame.frameType)
            ? "h-6"
            : "h-[10px]"),
      )}
      style={
        focused
          ? {
              top: -FOCUS_FRAME_DRAG_OUTSET * scale,
              height:
                (inFrameWindow
                  ? FOCUS_FRAME_DRAG_OUTSET
                  : FOCUS_FRAME_DRAG_HEIGHT) * scale,
            }
          : undefined
      }
      data-frame-drag-handle="true"
      data-frame-chrome-drag-strip="true"
      data-detached-frame-drag-handle="true"
      data-conversation-frame-header={isConversation ? "true" : undefined}
      data-testid="frame-drag-strip"
      tabIndex={0}
      role="group"
      aria-label={t("frameControls")}
      onFocus={(event) => {
        if (event.currentTarget.matches(":focus-visible"))
          onTopActiveChange(true);
      }}
      onBlur={() => onTopActiveChange(false)}
    />
  );
  const attentionDot = tone && (
    <div
      className="pointer-events-none absolute right-3 top-3 z-[81] flex"
      data-testid="frame-attention-dot"
    >
      <StatusDot tone={tone} label={stateText ?? undefined} />
    </div>
  );
  // The surface owns the chrome (the wall's Pinned tiles): the dot only.
  if (renderer.chrome === "none") return attentionDot || null;
  if (!focused && !auxiliary)
    return (
      <>
        {dragStrip(1)}
        {attentionDot}
      </>
    );
  const renderChrome = ({ inside, scale }: FrameChromeGeometry) => {
    const chromeInside = focused ? maximized : inside && !isConversation;
    const chromeStyle: CSSProperties = {
      top: chromeInside ? 8 * scale : -36 * scale,
      height: 28 * scale,
      pointerEvents: focused ? "none" : shown ? "auto" : "none",
    };
    return (
      <>
        {dragStrip(scale)}
        {attentionDot}
        <div
          className={cn(
            "absolute inset-x-0 z-[90] transition-opacity",
            shown ? "opacity-100" : "opacity-0",
          )}
          style={chromeStyle}
          data-frame-hover-actions="true"
          data-testid={`frame-hover-actions-${frameId}`}
          data-frame-chrome-placement={chromeInside ? "inside" : "above"}
          onPointerEnter={() => onTopActiveChange(true)}
          onPointerLeave={() => onTopActiveChange(false)}
          onFocusCapture={() => onTopActiveChange(true)}
          onBlurCapture={() => onTopActiveChange(false)}
          aria-hidden={!shown}
          inert={!shown}
        >
          {!chromeInside && (
            <div
              className="absolute inset-x-0 top-full"
              style={{
                height: 8 * scale,
                pointerEvents: focused ? "none" : undefined,
              }}
              data-testid="frame-chrome-bridge"
            />
          )}
          {auxiliary && !isConversation && (
            <span
              className="denker-frame-hover-pill absolute left-0 top-0 flex h-7 max-w-[55%] origin-top-left cursor-grab items-center gap-1.5 truncate rounded-full px-2.5 text-appkit-caption font-medium active:cursor-grabbing"
              style={{ transform: `scale(${scale})` }}
              title={tooltip}
              data-denker-glass-force={pillGlassForce}
              data-frame-drag-handle="true"
              data-detached-frame-drag-handle="true"
              data-testid="frame-chrome-title"
            >
              {tone && <StatusDot tone={tone} label={stateText ?? undefined} />}
              {hoverTitle}
            </span>
          )}
          <div
            className="denker-frame-hover-pill nodrag absolute right-0 top-0 flex h-7 origin-top-right items-center gap-0.5 rounded-full px-0.5"
            style={{
              transform: `scale(${scale})`,
              right: chromeInside ? 8 * scale : 0,
              pointerEvents: shown ? "auto" : "none",
            }}
            onPointerDown={(event) => event.stopPropagation()}
            data-denker-glass-force={pillGlassForce}
            data-frame-hover-right-target="true"
            data-testid="frame-chrome-buttons"
          >
            {leadTools && (
              <>
                {attentionShown && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7"
                    disabled={pending}
                    onClick={() => void handleAttention()}
                    data-testid={
                      attention?.kind === "question"
                        ? "frame-chrome-answer"
                        : "frame-chrome-dismiss"
                    }
                  >
                    {attention?.kind === "question"
                      ? t("answer")
                      : t("dismiss")}
                  </Button>
                )}
                {toolsShown && (
                  <FrameChromeTools
                    tools={config.tools}
                    onOpenChange={setToolOpen}
                  />
                )}
                {artifactsShown && (
                  <ConversationArtifactMenu
                    conversationFrameId={actionFrameId}
                    frame={frame}
                    open={artifactOpen}
                    onOpenChange={setArtifactOpen}
                    onArtifactPresented={onConversationArtifactPresented}
                    testIdFrameId={frameId}
                  />
                )}
                {editController.editing ? (
                  <Button
                    type="button"
                    size="iconSm"
                    disabled={editController.saving}
                    onClick={() => void editController.saveEdits()}
                    aria-label={t("saveFrameEdits")}
                    title={t("saveFrameEdits")}
                    data-testid="frame-chrome-save"
                  >
                    {editController.saving ? (
                      <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Icons.Check className="h-3.5 w-3.5" />
                    )}
                  </Button>
                ) : (
                  editController.available &&
                  actionButton(
                    "edit",
                    t("editFrame"),
                    Icons.Edit2,
                    editController.beginEditing,
                  )
                )}
                {anyTool && (
                  <span
                    aria-hidden="true"
                    className="mx-1 h-3.5 w-px shrink-0 bg-current opacity-20"
                    data-testid="frame-hover-tools-separator"
                  />
                )}
              </>
            )}
            {focused || auxiliary ? (
              <>
                {itemMenu?.({
                  frame,
                  extras: config.menuItems ?? NO_MENU_EXTRAS,
                })}
                {!chrome.backHidden &&
                  actionButton(
                    "back",
                    t("backToWorkspace"),
                    Icons.ArrowLeft,
                    handleBack,
                  )}
                {actionButton("close", t("closeFrame"), Icons.X, handleClose)}
              </>
            ) : null}
          </div>
        </div>
      </>
    );
  };
  if (auxiliary) return renderChrome(DETACHED_CHROME_GEOMETRY);
  return (
    <FrameChromeMeasure frameId={actionFrameId} rootRef={frameRootRef}>
      {renderChrome}
    </FrameChromeMeasure>
  );
});
